import React from 'react';
import { Box, List, ListItemButton, ListItemText, Tab, Tabs, useMediaQuery, useTheme } from '@mui/material';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config/routes';
import PageMeta from 'components/seo/PageMeta';
import ErrorBoundary from 'components/feedback/ErrorBoundary';

const accountLinks = [
  { labelKey: 'account.title', path: ROUTES.ACCOUNT },
  { labelKey: 'account.profile', path: ROUTES.PROFILE },
  { labelKey: 'account.addresses', path: ROUTES.ADDRESSES },
  { labelKey: 'account.orders', path: ROUTES.ORDERS },
  { labelKey: 'account.pendingOrders', path: ROUTES.ORDERS_PENDING },
  { labelKey: 'account.quickOrder', path: ROUTES.QUICK_ORDER },
  { labelKey: 'account.favorites', path: ROUTES.FAVORITES },
  { labelKey: 'account.currentAccount', path: ROUTES.CURRENT_ACCOUNT },
  { labelKey: 'account.paymentNotices', path: ROUTES.PAYMENT_NOTICES },
  { labelKey: 'account.history', path: ROUTES.HISTORY },
];

const AccountLayout: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const currentIndex = Math.max(
    0,
    accountLinks.findIndex((link) => location.pathname === link.path)
  );

  return (
    <>
      <PageMeta title={t('account.title')} />
      <Box
        display="flex"
        gap={{ xs: 2, md: 4 }}
        flexDirection={{ xs: 'column', md: 'row' }}
        alignItems="stretch"
      >
        {isMobile ? (
          <Tabs
            value={currentIndex}
            onChange={(_, index: number) => navigate(accountLinks[index].path)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label={t('account.title')}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 1,
              minHeight: 44,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 },
            }}
          >
            {accountLinks.map((link) => (
              <Tab key={link.path} label={t(link.labelKey)} />
            ))}
          </Tabs>
        ) : (
          <Box
            component="nav"
            aria-label={t('account.title')}
            sx={{
              width: 220,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              pr: 2,
            }}
          >
            <List disablePadding>
              {accountLinks.map((link) => {
                const selected = location.pathname === link.path;
                return (
                  <ListItemButton
                    key={link.path}
                    selected={selected}
                    onClick={() => navigate(link.path)}
                    sx={{
                      borderRadius: 2,
                      mb: 0.5,
                      '&.Mui-selected': {
                        bgcolor: 'action.selected',
                        color: 'primary.main',
                        fontWeight: 700,
                      },
                    }}
                  >
                    <ListItemText
                      primary={t(link.labelKey)}
                      primaryTypographyProps={{ fontWeight: selected ? 700 : 500 }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        )}
        <Box flexGrow={1} minWidth={0}>
          <ErrorBoundary variant="page">
            <Outlet />
          </ErrorBoundary>
        </Box>
      </Box>
    </>
  );
};

export default AccountLayout;
